import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, withModifiers as _withModifiers, TransitionGroup as _TransitionGroup, withCtx as _withCtx, renderSlot as _renderSlot, mergeProps as _mergeProps, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = {
  key: 1,
  class: "message-reaction-controls"
}
const _hoisted_3 = ["onMousedown"]
const _hoisted_4 = { class: "reaction-control-right" }
const _hoisted_5 = { class: "reaction-control-text" }

import {
  getIsAutomaticMessageType,
  getIsMessageFriendRequestType,
  getIsMessageTextType,
  getIsMyMessage,
} from "@/utils/message";
import { FriendRequestStatus, Message, Positions } from "@/store/chats/types";
import { useComputedValue, useGetMessageHandlers } from "@/composables";
import { GetterTypes } from "@/store";
import { computed, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { ChatsMutationTypes } from "@/store/chats";
import { IconType } from "@/types/icons";
import Icon from "@/components/icons/Icon/Icon.vue";
import { copyTextToClipboard } from "@/utils/app";
import BackdropModal from "@/components/modals/BackdropModal/BackdropModal.vue";
import MessageWithReactions from "../MessageWithReactions/MessageWithReactions.vue";
import MessageEmojiReactionsPanel from "../MessageEmojiReactionsPanel/MessageEmojiReactionsPanel.vue";
import { LayoutTypes } from "@/store/app/state";
import { convertNumberToPx } from "@/utils/modifiers";
import { useI18n } from "vue-i18n";

interface ChatMessageReactionsProps {
  message: Message;
  isActive: boolean;
  hasShadow: boolean;
  isModalOpen: boolean;
  modalId: string;
  elementId: string;
  position: Positions;
  isTouchEndEnabled: boolean;
}

interface Emits {
  (e: "hideShadow"): void;
  (e: "openModal"): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ChatMessageReactions',
  props: {
    message: {},
    isActive: { type: Boolean },
    hasShadow: { type: Boolean },
    isModalOpen: { type: Boolean },
    modalId: {},
    elementId: {},
    position: {},
    isTouchEndEnabled: { type: Boolean }
  },
  emits: ["hideShadow", "openModal"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const {
  handleReplyToMessage,
  handleModifyMessage,
  handleRemoveMessage,
  openForwardMessageDialog,
} = useGetMessageHandlers();
const { commit } = useStore();
const { t } = useI18n();
const props = __props;
const authId = useComputedValue<number>(GetterTypes.GET_AUTH_ID);
const isMine = computed(() => getIsMyMessage(props.message, authId.value));
const messageContainer = ref<HTMLDivElement>();
const isFriendRequestType = computed(() =>
  getIsMessageFriendRequestType(props.message),
);
const isPendingFriendRequest = computed(
  () =>
    getIsMessageFriendRequestType(props.message) &&
    props.message.status === FriendRequestStatus.Pending &&
    !props.message.isMine,
);
const isTextMessage = computed(() => getIsMessageTextType(props.message));
const isControlsActive = computed(() => props.isActive && !props.isModalOpen);
const messageReactionId = useComputedValue<string | null>(
  GetterTypes.GET_MESSAGE_REACTION_ID,
);
const isReacting = computed(() => !!messageReactionId.value);
const messageWithReactionsProps = computed(() => ({
  message: props.message,
  isMine: isMine.value,
  elementId: props.elementId,
}));
const layoutType = useComputedValue<LayoutTypes>(GetterTypes.GET_LAYOUT_TYPE);
const isMobile = computed(() => layoutType.value === LayoutTypes.MOBILE);
const hasReactions = computed(
  () =>
    !getIsAutomaticMessageType(props.message) &&
    props.message.reactions?.visibleItems?.length,
);

const contentStyle = computed(() => {
  if (!isControlsActive.value) {
    return;
  }
  const { height, left, width, top } = props.position;
  return {
    left: convertNumberToPx(left),
    width: convertNumberToPx(width),
    height: convertNumberToPx(height),
    top: convertNumberToPx(top),
  };
});

watch(isReacting, (newVal) => {
  if (!newVal || !props.isActive) {
    return;
  }

  window.addEventListener("mousedown", onClickOutside, { capture: true });
});

onUnmounted(() => {
  clearClickOutsideEventListener();
});

const clearClickOutsideEventListener = () => {
  window.removeEventListener("mousedown", onClickOutside, { capture: true });
};
const getIsContainingElement = (
  element: HTMLDivElement,
  wrapper: HTMLElement | undefined | null,
) => Boolean(wrapper === element || wrapper?.contains(element));

const onClickOutside = (e: MouseEvent) => {
  const target = e.target as HTMLDivElement;
  // this is due to unability to pass container reference to the modal
  const modal = document.getElementById(props.modalId);

  if (
    getIsContainingElement(target, messageContainer.value) ||
    getIsContainingElement(target, modal)
  ) {
    return;
  }
  closeControls();
};

const clearReactionId = () => {
  commit(ChatsMutationTypes.SET_MESSAGE_REACTION_ID, {
    messageId: null,
  });
};

const closeControls = () => {
  if (!props.isTouchEndEnabled) {
    return;
  }
  clearReactionId();
  clearClickOutsideEventListener();
  emit("hideShadow");
};

const copyMessageToClipboard = () => {
  if (!getIsMessageTextType(props.message) || !props.isTouchEndEnabled) {
    return;
  }
  closeControls();
  copyTextToClipboard(props.message.messageText);
};

const openReactionsModal = () => {
  if (isControlsActive.value) {
    return;
  }
  emit("openModal");
};

const replyToMessage = () => {
  handleReplyToMessage(props.message);
  closeControls();
};

const modifyMessage = () => {
  handleModifyMessage(props.message);
  closeControls();
};

const removeMessage = () => {
  handleRemoveMessage(props.message);
  closeControls();
};

const forwardMessage = () => {
  openForwardMessageDialog(props.message);
  closeControls();
};

const controls = computed(() => {
  const result = [
    {
      icon: IconType.REPLY_THIN,
      text: t("chat.chatContainer.messages.controls.reply"),
      action: replyToMessage,
    },
  ];
  if (isTextMessage.value) {
    result.push({
      icon: IconType.COPY,
      text: t("chat.chatContainer.messages.controls.copy"),
      action: copyMessageToClipboard,
    });
  }
  if (
    getIsMessageTextType(props.message) &&
    isMine.value &&
    !props.message.isForwarded
  ) {
    result.push({
      icon: IconType.EDIT,
      text: t("chat.chatContainer.messages.controls.modify"),
      action: modifyMessage,
    });
  }

  if (!getIsAutomaticMessageType(props.message) && isMine.value) {
    result.push({
      icon: IconType.REMOVE_MESSAGE,
      text: t("chat.chatContainer.messages.controls.delete"),
      action: removeMessage,
    });
  }
  if (!getIsAutomaticMessageType(props.message)) {
    result.push({
      icon: IconType.FORWARD_MESSAGE,
      text: t("chat.chatContainer.messages.controls.forward"),
      action: forwardMessage,
    });
  }
  return result;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["message-content-container", {
      isMine: isMine.value,
      isFriendRequestType: isFriendRequestType.value,
      isPendingFriendRequest: isPendingFriendRequest.value,
    }])
  }, [
    _createVNode(BackdropModal, { enabled: _ctx.hasShadow }, {
      default: _withCtx(() => [
        _createElementVNode("div", {
          id: _ctx.elementId,
          ref_key: "messageContainer",
          ref: messageContainer,
          class: _normalizeClass(["mess-content", {
          isFixed: _ctx.hasShadow,
          isFriendRequestType: isFriendRequestType.value,
          isMine: isMine.value,
          hasReactions: hasReactions.value,
        }]),
          style: _normalizeStyle(contentStyle.value)
        }, [
          (isMobile.value)
            ? (_openBlock(), _createBlock(_TransitionGroup, {
                key: 0,
                name: "pop"
              }, {
                default: _withCtx(() => [
                  (isControlsActive.value)
                    ? (_openBlock(), _createBlock(MessageEmojiReactionsPanel, {
                        key: 0,
                        "message-id": _ctx.message.id,
                        "is-mine": isMine.value,
                        onClosePanel: closeControls
                      }, null, 8, ["message-id", "is-mine"]))
                    : _createCommentVNode("", true),
                  (isControlsActive.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(controls.value, (control) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: control.icon,
                            class: "reaction-control-item",
                            onMousedown: _withModifiers(control.action, ["left"])
                          }, [
                            _createVNode(Icon, {
                              class: "icon",
                              icon: control.icon
                            }, null, 8, ["icon"]),
                            _createElementVNode("div", _hoisted_4, [
                              _createElementVNode("span", _hoisted_5, _toDisplayString(control.text), 1),
                              _createVNode(Icon, {
                                class: "icon",
                                icon: _unref(IconType).FORWARD
                              }, null, 8, ["icon"])
                            ])
                          ], 40, _hoisted_3))
                        }), 128))
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(MessageWithReactions, _mergeProps(messageWithReactionsProps.value, { onOpenModal: openReactionsModal }), {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "default")
            ]),
            _: 3
          }, 16)
        ], 14, _hoisted_1)
      ]),
      _: 3
    }, 8, ["enabled"]),
    (_ctx.hasShadow && isMobile.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["mess-content", { isFriendRequestType: isFriendRequestType.value }])
        }, [
          _createVNode(MessageWithReactions, _normalizeProps(_guardReactiveProps(messageWithReactionsProps.value)), {
            default: _withCtx(() => [
              _renderSlot(_ctx.$slots, "default")
            ]),
            _: 3
          }, 16)
        ], 2))
      : _createCommentVNode("", true),
    _renderSlot(_ctx.$slots, "modal"),
    _renderSlot(_ctx.$slots, "checkboxSelectMessage")
  ], 2))
}
}

})